import { Spacing } from '@persona-id/design-tokens';
import { memo, ReactNode, VFC } from 'react';
import styled from 'styled-components';

import {
  CONDITIONAL_NODE_HEIGHT,
  CONDITIONAL_NODE_WIDTH,
} from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode/ConditionalNode';
import { Z_INDICES } from 'dashboard/styles/variables';
import { Label, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

const Node = styled.div<{
  selected: boolean;
  hasError?: boolean;
}>`
  display: grid;
  place-items: center;
  height: ${CONDITIONAL_NODE_HEIGHT}px;
  width: ${CONDITIONAL_NODE_WIDTH}px;
  padding: 0 ${Spacing[3]};
  border: 2px solid;
  border-radius: ${Spacing[2]};
  box-shadow: ${Elevation.Mid};
  user-select: none;
  z-index: ${Z_INDICES.withShadow};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-color: ${({ theme, selected, hasError }) => {
    if (hasError) {
      return theme.color.variants.danger.highlight;
    } else if (selected) {
      return theme.color.primary.brand600;
    } else {
      return theme.color.variants.primary.highlight;
    }
  }};
  &:focus:enabled {
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`;

const Name = styled(Label).attrs(() => ({ size: 'lg', weight: 'bold' }))`
  display: -webkit-box;
  white-space: normal;
  text-align: center;
  line-height: 20px;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

interface ScreenTransitionNodeProps {
  title: string;
  description?: string;
  selected?: boolean;
  hasError?: boolean;
  nodeHighlighter?: ReactNode;
}

/**
 * This component is responsible for rendering a ScreenTransitionNode
 * representing a connection from a screen with multiple
 * transitions (multiple components and/or workflow error steps) to one of its branches
 */
const ScreenTransitionNode: VFC<ScreenTransitionNodeProps> = ({
  title,
  description,
  nodeHighlighter,
  selected = false,
  hasError = false,
}) => {
  return (
    <Tooltip
      side='top'
      trigger={
        <Node data-test='screen-transition-node' selected={selected} hasError={hasError}>
          {nodeHighlighter}
          <Name>{title}</Name>
        </Node>
      }
    >
      <Paragraph data-test='screen-transition-node__tooltip' size='sm'>
        {description ?? title}
      </Paragraph>
    </Tooltip>
  );
};

export default memo(ScreenTransitionNode);
